import { useRef, useState } from "react";
import { Button, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import { AttachFile, Delete, Download } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import { useSnackbar } from "notistack";
import { nanoid } from "nanoid";
import { useQueryClient } from "@/compat/reactQuery";
import queryKeys from "../../../constants/queryKeys";
import { acceptedAttachmentsAttribute } from "../../constants/attachmentFileTypes";
import { attachmentUploadError } from "../../utils/attachmentErrors";
import { downloadFileFromBlob } from "../../../utils/fileUtils";
import riskAssessmentAttachmentService from "../../services/riskAssessmentAttachmentService";
import useRiskAssessmentAttachments from "../../hooks/queries/useRiskAssessmentAttachments";
import useUploadRiskAssessmentAttachments from "../../hooks/mutations/useUploadRiskAssessmentAttachments";
import useDeleteRiskAssessmentAttachment from "../../hooks/mutations/useDeleteRiskAssessmentAttachment";
import type { RiskAssessmentAttachmentDTO } from "../../types/riskAssessmentAttachmentDtos";

const useStyles = makeStyles((theme) => ({
    header: { alignItems: "center", display: "flex", gap: theme.spacing(1), justifyContent: "space-between" },
    list: { display: "flex", flexDirection: "column", marginTop: theme.spacing(1) },
    row: { alignItems: "center", display: "flex", gap: theme.spacing(1) },
    name: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    empty: { color: theme.palette.text.secondary },
    hidden: { display: "none" },
}));

interface RiskAssessmentAttachmentsProps {
    riskAssessmentId: number;
    // Set to scope the panel to one hazard; omitted, it shows assessment-level files.
    riskAssessmentHazardId?: number | null;
    canEdit?: boolean;
    title?: string;
}

export default function RiskAssessmentAttachments({
    riskAssessmentId,
    riskAssessmentHazardId = null,
    canEdit = true,
    title = "Attachments",
}: RiskAssessmentAttachmentsProps) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const fileInput = useRef<HTMLInputElement>(null);
    const [progress, setProgress] = useState<number | null>(null);

    const attachments = useRiskAssessmentAttachments(riskAssessmentId);
    const upload = useUploadRiskAssessmentAttachments();
    const remove = useDeleteRiskAssessmentAttachment();

    const all: RiskAssessmentAttachmentDTO[] = Array.isArray(attachments?.data) ? attachments.data : [];
    const rows = all.filter((x) => (x.riskAssessmentHazardId ?? null) === riskAssessmentHazardId);

    const refresh = () =>
        queryClient.invalidateQueries({
            queryKey: [queryKeys.riskAssessmentAttachments, riskAssessmentId],
        });

    const onFilesPicked = (fileList?: FileList | null) => {
        if (!fileList?.length) return;

        const files = [...fileList].map((file) => ({ syncId: nanoid(5), file }));

        upload.mutate(
            {
                riskAssessmentId,
                riskAssessmentHazardId,
                files,
                onUploadProgress: (e: any) =>
                    setProgress(e.total ? Math.round((e.loaded * 100) / e.total) : null),
            },
            {
                onSuccess: () => {
                    setProgress(null);
                    if (fileInput.current) fileInput.current.value = "";
                    refresh();
                },
                onError: (error: any) => {
                    setProgress(null);
                    enqueueSnackbar(attachmentUploadError(error), { variant: "error" });
                },
            }
        );
    };

    const download = async (attachment: RiskAssessmentAttachmentDTO) => {
        try {
            const bytes = await riskAssessmentAttachmentService.downloadAttachment(attachment.reference);
            downloadFileFromBlob(bytes, attachment.fileName, attachment.contentType);
        } catch {
            enqueueSnackbar("Could not download the file. Please try again.", { variant: "error" });
        }
    };

    const deleteAttachment = (attachment: RiskAssessmentAttachmentDTO) =>
        remove.mutate(attachment.id, {
            onSuccess: () => refresh(),
            onError: () => enqueueSnackbar("Could not remove the file. Please try again.", { variant: "error" }),
        });

    return (
        <div>
            <div className={classes.header}>
                <Typography variant="subtitle2">{title}</Typography>
                {canEdit && (
                    <Button
                        size="small"
                        color="primary"
                        startIcon={<AttachFile />}
                        disabled={upload.isLoading}
                        onClick={() => fileInput.current?.click()}
                    >
                        Add file
                    </Button>
                )}
                <input
                    ref={fileInput}
                    className={classes.hidden}
                    type="file"
                    multiple
                    accept={acceptedAttachmentsAttribute}
                    onChange={(e) => onFilesPicked(e.target.files)}
                />
            </div>

            {progress !== null && <LinearProgress variant="determinate" value={progress} />}

            <div className={classes.list}>
                {rows.length === 0 ? (
                    <Typography variant="caption" className={classes.empty}>
                        No files attached.
                    </Typography>
                ) : (
                    rows.map((attachment) => (
                        <div key={attachment.id} className={classes.row}>
                            <Typography variant="body2" className={classes.name} title={attachment.fileName}>
                                {attachment.fileName}
                            </Typography>
                            <Tooltip title="Download">
                                <IconButton size="small" onClick={() => download(attachment)}>
                                    <Download fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {canEdit && (
                                <Tooltip title="Remove">
                                    <IconButton
                                        size="small"
                                        disabled={remove.isLoading}
                                        onClick={() => deleteAttachment(attachment)}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
